/**
 * Centralized logging service for the application
 * 
 * This service provides environment-aware logging with different levels.
 * In production, sensitive information is filtered out and errors can be
 * sent to external monitoring services.
 * 
 * Log levels can be controlled via VITE_LOG_LEVEL environment variable:
 * - debug: Show all logs
 * - info: Show info, warn, and error logs
 * - warn: Show warn and error logs
 * - error: Show only error logs
 * - none: Disable all logs
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

interface LogContext {
  [key: string]: unknown;
}

// Log level priorities (lower number = more verbose)
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4
};

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private isProduction = import.meta.env.PROD;
  private currentLogLevel: number;

  constructor() {
    // Get log level from environment variable or use defaults
    const envLogLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel) || 
                       (this.isProduction ? 'error' : 'info');
    
    this.currentLogLevel = LOG_LEVELS[envLogLevel] ?? LOG_LEVELS.info;

    // Log the current configuration on initialization (only in development)
    if (this.isDevelopment && this.shouldLog('info')) {
      // eslint-disable-next-line no-console
      console.info(`[Logger] Initialized with level: ${envLogLevel} (${this.currentLogLevel})`);
    }
  }

  /**
   * Check if a log level should be output based on current configuration
   */
  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= this.currentLogLevel;
  }

  /**
   * Mask sensitive string values
   */
  private maskSensitiveValue(value: string, type: string): string {
    if (!value || typeof value !== 'string') return value;
    
    switch(type) {
      case 'email':
        // user@example.com → use***@example.com
        const emailParts = value.split('@');
        if (emailParts.length === 2) {
          const username = emailParts[0];
          const masked = username.length > 3 
            ? username.substring(0, 3) + '***' 
            : '***';
          return `${masked}@${emailParts[1]}`;
        }
        return '***@***';
        
      case 'id':
        // 12345678 → 123***78
        if (value.length > 5) {
          return value.substring(0, 3) + '***' + value.substring(value.length - 2);
        }
        return '***';
        
      case 'token':
        // Show only first 10 characters
        return value.length > 10 ? value.substring(0, 10) + '***' : '***';
        
      case 'url':
        // Remove query parameters
        try {
          const url = new URL(value);
          return `${url.protocol}//${url.host}${url.pathname}`;
        } catch {
          return value.substring(0, 20) + '***';
        }
        
      default:
        // Default masking
        return '[REDACTED]';
    }
  }

  /**
   * Filters sensitive data from log context
   */
  private sanitizeContext(context?: LogContext): LogContext | undefined {
    if (!context) return context;
    
    const sensitivePatterns = [
      { keys: ['password', 'pwd', 'pass'], type: 'password' },
      { keys: ['token', 'apikey', 'api_key', 'authtoken', 'auth_token'], type: 'token' },
      { keys: ['email', 'mail'], type: 'email' },
      { keys: ['userid', 'user_id', 'contactid', 'contact_id', 'erpcontactid'], type: 'id' },
      { keys: ['url', 'endpoint', 'api_url'], type: 'url' },
      { keys: ['address', 'phone', 'tel', 'mobile'], type: 'personal' },
      { keys: ['creditcard', 'credit_card', 'cvv', 'ssn'], type: 'financial' }
    ];
    
    const sanitized = { ...context };
    
    Object.keys(sanitized).forEach(key => {
      const lowerKey = key.toLowerCase();
      const pattern = sensitivePatterns.find(p => 
        p.keys.some(k => lowerKey.includes(k))
      );
      
      if (pattern) {
        const value = sanitized[key];
        if (typeof value === 'string') {
          sanitized[key] = this.maskSensitiveValue(value, pattern.type);
        } else if (pattern.type === 'password' || pattern.type === 'financial') {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = '[MASKED]';
        }
      }
    });
    
    return sanitized;
  }

  /**
   * Debug level logging
   */
  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog('debug')) return;
    
    const timestamp = new Date().toISOString();
    const sanitizedContext = this.isProduction ? this.sanitizeContext(context) : context;
    // eslint-disable-next-line no-console
    console.log(`[${timestamp}] [DEBUG] ${message}`, sanitizedContext || '');
  }

  /**
   * Info level logging
   */
  info(message: string, context?: LogContext): void {
    if (!this.shouldLog('info')) return;
    
    const timestamp = new Date().toISOString();
    const sanitizedContext = this.isProduction ? this.sanitizeContext(context) : context;
    // eslint-disable-next-line no-console
    console.info(`[${timestamp}] [INFO] ${message}`, sanitizedContext || '');
  }

  /**
   * Warning level logging
   */
  warn(message: string, context?: LogContext): void {
    if (!this.shouldLog('warn')) return;
    
    const timestamp = new Date().toISOString();
    const sanitizedContext = this.isProduction ? this.sanitizeContext(context) : context;
    console.warn(`[${timestamp}] [WARN] ${message}`, sanitizedContext || '');
  }

  /**
   * Error level logging
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (!this.shouldLog('error')) return;
    
    const sanitizedContext = this.isProduction 
      ? this.sanitizeContext(context) 
      : context;

    const timestamp = new Date().toISOString();
    
    if (this.isProduction) {
      // In production, send to error tracking service with sanitized data
      console.error(`[${timestamp}] [ERROR] ${message}`, {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        context: sanitizedContext
      });
      
      // TODO: Integrate with error tracking service like Sentry
      // Sentry.captureException(error, { extra: sanitizedContext });
    } else {
      // In development, show full error details
      console.error(`[${timestamp}] [ERROR] ${message}`, error, context);
    }
  }

  /**
   * Critical error logging - for security violations and critical failures
   * Critical errors bypass log level settings
   */
  critical(message: string, error?: Error | unknown, context?: LogContext): void {
    // Critical errors are always logged, regardless of log level
    const sanitizedContext = this.sanitizeContext(context);
    const timestamp = new Date().toISOString();
    
    console.error(`[${timestamp}] [CRITICAL] ${message}`, {
      message: error instanceof Error ? error.message : 'Critical error',
      stack: error instanceof Error ? error.stack : undefined,
      context: sanitizedContext
    });
    
    // TODO: Send immediate alert in production
    // alertingService.sendCriticalAlert(message, error, sanitizedContext);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for use in other files
export type { LogLevel, LogContext };